<template>
  <v-card outlined class="rounded-lg fill-height">
    <v-card-text class="pa-4">

      <!-- Header -->
      <div class="d-flex align-center mb-4">
        <v-avatar size="40" color="primary" class="mr-3" rounded>
          <v-icon color="white">mdi-robot</v-icon>
        </v-avatar>
        <div>
          <div class="subtitle-1 font-weight-bold">AI Coach</div>
          <div class="caption grey--text">Daily improvement engine — grounded in your data</div>
        </div>
        <v-spacer />
        <v-btn
          color="primary"
          depressed
          small
          rounded
          class="white--text text-capitalize font-weight-bold"
          @click="openAiSuggestPanel()"
        >
          <v-icon small class="mr-1">mdi-shimmer</v-icon>
          AI Suggest
        </v-btn>
      </div>

      <!-- AI Suggest Panel -->
      <v-expand-transition>
        <div v-if="showAiSuggestPanel" class="mb-4">
          <div class="rounded-lg pa-3 grey lighten-4">
            <div class="caption font-weight-bold mb-3 grey--text text--darken-1" style="letter-spacing:0.08em;">
              AI SUGGESTED COACHING PROMPTS
            </div>
            <div v-if="aiSuggestionsLoading" class="d-flex align-center justify-center py-4">
              <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2" />
              <span class="caption grey--text">Generating suggestions...</span>
            </div>
            <div
              v-for="(sug, i) in aiSuggestions"
              :key="i"
              class="d-flex align-start mb-3 pb-3"
              :style="i < aiSuggestions.length - 1 ? 'border-bottom:1px solid #e0e0e0;' : ''"
            >
              <v-icon small color="primary" class="mr-2 mt-1">mdi-shimmer</v-icon>
              <div class="flex-grow-1 mr-2">
                <div class="body-2 font-weight-medium">{{ sug.text }}</div>
                <div class="caption grey--text mt-1" style="font-style:italic;">{{ sug.reason }}</div>
              </div>
              <v-btn
                small
                color="primary"
                depressed
                rounded
                class="white--text text-capitalize flex-shrink-0"
                style="min-width:60px;"
                @click="selectPrompt(sug.text); showAiSuggestPanel = false"
              >
                Ask
              </v-btn>
            </div>
            <div v-if="aiSuggestions.length > 0 && !aiSuggestionsLoading" class="d-flex justify-center mt-1">
              <v-btn
                small
                text
                color="primary"
                class="text-capitalize"
                :loading="aiSuggestionsLoading"
                @click="regenAiSuggestions"
              >
                <v-icon small class="mr-1">mdi-refresh</v-icon>
                Generate new suggestions
              </v-btn>
            </div>
          </div>
        </div>
      </v-expand-transition>

      <!-- Missing context banner -->
      <div
        v-if="showContextWarning"
        class="rounded-lg pa-3 mb-4"
        style="background:#fff8e1;border:1px solid #ffe082;"
      >
        <div class="d-flex align-center mb-2">
          <v-icon small color="amber darken-2" class="mr-2">mdi-alert-outline</v-icon>
          <span class="caption font-weight-bold amber--text text--darken-2" style="letter-spacing:0.06em;">
            MISSING CONTEXT — COACH QUALITY IS LIMITED
          </span>
        </div>
        <div v-if="!hasKpis" class="caption mb-1">
          <span class="font-weight-bold">KPIs:</span> Add 2–3 KPIs in the KPI Tracker so coaching can target what matters.
        </div>
        <div v-if="!hasHabits" class="caption mb-1">
          <span class="font-weight-bold">Habits:</span> Track at least one daily habit to ground feedback in routines.
        </div>
        <div v-if="!hasRole" class="caption mb-1">
          <span class="font-weight-bold">Role profile:</span> Fill in your role + responsibilities so advice fits your job.
        </div>
        <div v-if="!hasRecentWin" class="caption mb-3">
          <span class="font-weight-bold">Accomplishments:</span> Log a recent win — coaching uses it for momentum cues.
        </div>
        <v-btn
          depressed
          outlined
          small
          rounded
          color="amber darken-2"
          class="text-capitalize font-weight-medium amber--text text--darken-2"
          style="width:100%;"
          @click="dismissWarning"
        >
          <v-icon small class="mr-1">mdi-close</v-icon>
          Dismiss for now
        </v-btn>
      </div>

      <!-- Prompt chips -->
      <div
        v-for="prompt in aiPrompts"
        :key="prompt"
        class="d-flex align-center pa-3 rounded-lg mb-2"
        :style="activePrompt === prompt
          ? 'background:#e3f2fd;border:1px solid #90caf9;cursor:pointer;'
          : 'background:#f5f5f5;border:1px solid transparent;cursor:pointer;'"
        @click="selectPrompt(prompt)"
      >
        <v-icon small color="primary" class="mr-3 flex-shrink-0">mdi-shimmer</v-icon>
        <span class="body-2 flex-grow-1">{{ prompt }}</span>
        <v-icon small color="grey">mdi-chevron-right</v-icon>
      </div>

      <!-- AI thinking -->
      <v-expand-transition>
        <div v-if="aiThinking" class="mt-3 pa-3 rounded-lg blue lighten-5 d-flex align-center" style="border:1px solid #90caf9;">
          <v-progress-circular indeterminate size="16" width="2" color="primary" class="mr-2" />
          <span class="caption grey--text text--darken-1">Coach is thinking...</span>
        </div>
      </v-expand-transition>

      <!-- Coach reply -->
      <v-expand-transition>
        <div
          v-if="aiReply && !aiThinking"
          class="mt-3 pa-3 rounded-lg blue lighten-5"
          style="border:1px solid #90caf9;"
        >
          <div class="caption font-weight-bold mb-1 grey--text text--darken-1" style="letter-spacing:0.08em;">
            COACH RESPONSE
          </div>
          <div class="body-2" style="line-height:1.7;" v-html="renderMarkdown(aiReply)" />
          <v-btn x-small text class="text-capitalize grey--text mt-2 pa-0" @click="aiReply = null; activePrompt = null">
            Dismiss
          </v-btn>
        </div>
      </v-expand-transition>

      <!-- Free-text input -->
      <div class="mt-3" style="position:relative;">
        <v-textarea
          v-model="aiQuery"
          placeholder="Ask anything — coach knows your tasks, KPIs, habits..."
          dense
          outlined
          hide-details
          rows="2"
          auto-grow
          class="body-2"
        />
        <v-btn
          fab
          x-small
          color="primary"
          class="white--text"
          style="position:absolute;bottom:8px;right:8px;"
          :disabled="!aiQuery.trim() || aiThinking"
          @click="sendQuery"
        >
          <v-icon small>mdi-send</v-icon>
        </v-btn>
      </div>

      <!-- Session history -->
      <div v-if="sessionHistory.length > 0" class="mt-4">
        <div
          class="d-flex align-center justify-space-between caption grey--text text--darken-1 mb-2"
          style="cursor:pointer;"
          @click="showHistory = !showHistory"
        >
          <span><v-icon x-small class="mr-1">mdi-history</v-icon>Past sessions ({{ sessionHistory.length }})</span>
          <v-icon x-small>{{ showHistory ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <v-expand-transition>
          <div v-if="showHistory">
            <div
              v-for="(session, idx) in sessionHistory.slice(0, 5)"
              :key="idx"
              class="pa-2 mb-2 rounded"
              style="background:#fafafa;border:1px solid #eeeeee;"
            >
              <div class="caption font-weight-bold mb-1 text-truncate" style="max-width:100%;">{{ session.prompt }}</div>
              <div class="caption grey--text text--darken-1 mb-1" style="line-height:1.4;">{{ session.response }}</div>
              <div class="caption grey--text" style="font-size:10px;">{{ formatDate(session.created_at) }}</div>
            </div>
          </div>
        </v-expand-transition>
      </div>

    </v-card-text>
  </v-card>
</template>

<script>
import { askAI, parseAIJson, buildUserContext } from '@/services/growthAiService';

export default {
  name: 'AiCoachCard',
  inject: {
    personalTasks: { default: () => () => [] },
    assignedTasks: { default: () => () => [] },
    allManagers:   { default: () => () => [] },
    roles:         { default: () => () => [] },
    users:         { default: () => () => [] },
  },
  data() {
    return {
      loading: false,
      aiThinking: false,
      activePrompt: null,
      aiQuery: '',
      aiReply: null,
      warningDismissed: false,
      sessionHistory: [],
      showHistory: false,
      // loaded from API for context completeness checks
      kpis: [],
      habits: [],
      growthAreas: [],
      // AI Suggest panel
      showAiSuggestPanel: false,
      aiSuggestions: [],
      aiSuggestionsLoading: false,
    };
  },
  computed: {
    hasKpis() {
      return this.kpis.length > 0;
    },
    hasHabits() {
      return this.habits.length > 0;
    },
    hasRole() {
      const user = this.$store.state.auth?.user;
      return !!(user?.position || user?.role_name || user?.role_id);
    },
    hasRecentWin() {
      const all = [...(this.personalTasks ? this.personalTasks() : []), ...(this.assignedTasks ? this.assignedTasks() : [])];
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000);
      return all.some(t => t.completed_at && new Date(t.completed_at) > sevenDaysAgo);
    },
    missingItems() {
      const items = [];
      if (!this.hasKpis) items.push('kpis');
      if (!this.hasHabits) items.push('habits');
      if (!this.hasRole) items.push('role');
      return items;
    },
    showContextWarning() {
      // Only show if there are actually missing items the user can fix (KPIs, habits, role).
      // Recent wins are nice-to-have, don't nag about them.
      return !this.warningDismissed && this.missingItems.length > 0;
    },
    storeUser() {
      return this.$store.state.auth?.user || {};
    },
    allTasks() {
      const p = this.personalTasks ? this.personalTasks() : [];
      const a = this.assignedTasks ? this.assignedTasks() : [];
      return [...p, ...a].filter(t => !t.deleted_at);
    },
    todayStr() {
      return new Date().toISOString().slice(0, 10);
    },
    overdueTasks() {
      const today = this.todayStr;
      return this.allTasks.filter(t => !t.completed_at && t.due_date && String(t.due_date).slice(0, 10) < today);
    },
    dueTodayTasks() {
      const today = this.todayStr;
      return this.allTasks.filter(t => !t.completed_at && t.due_date && String(t.due_date).slice(0, 10) === today);
    },
    inProgressTasks() {
      return this.allTasks.filter(t => !t.completed_at && (t.status?.name || '').toLowerCase().includes('progress'));
    },
    highPriorityPending() {
      return this.allTasks.filter(t => !t.completed_at && (t.priority_id === 1 || t.priority_id === 2));
    },
    completedThisWeek() {
      const weekAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString().slice(0, 10);
      return this.allTasks.filter(t => t.completed_at && String(t.completed_at).slice(0, 10) >= weekAgo);
    },
    // Pending KPIs the user still has to finish this week
    pendingKpis() {
      // Only "this_week" batch should count; kpis array is flattened with last_week entries too.
      return (this.kpis || []).filter(k => !k.is_done);
    },
    // Roles the user is a manager of (by name)
    managedRoleNames() {
      const uid = this.storeUser.id;
      const managers = typeof this.allManagers === 'function' ? this.allManagers() : [];
      const rolesList = typeof this.roles === 'function' ? this.roles() : [];
      if (!Array.isArray(managers) || !uid) return [];
      const managedIds = managers
        .filter(m => m.user_id === uid && (m.is_manager === true || m.is_manager === 1 || m.is_manager === undefined))
        .map(m => m.user_role_id);
      return managedIds
        .map(id => (Array.isArray(rolesList) ? rolesList.find(r => r.id === id)?.name : null))
        .filter(Boolean);
    },
    // Primary role label for prompt phrasing (prefers team name over legacy role_id)
    primaryRoleLabel() {
      const rm = Array.isArray(this.storeUser.role_members) ? this.storeUser.role_members : [];
      if (rm.length > 0) {
        const name = rm[0].role?.name;
        if (name) return name;
      }
      const rid = this.storeUser.role_id;
      if (rid === 1 || rid === 11) return 'Admin';
      if (rid === 2) return 'Manager';
      return 'Employee';
    },
    isPeopleManager() {
      return this.managedRoleNames.length > 0 || this.storeUser.role_id === 1 || this.storeUser.role_id === 2 || this.storeUser.role_id === 11;
    },
    // All role IDs + names associated with the user (team membership via role_members).
    userRoleIds() {
      const rm = Array.isArray(this.storeUser.role_members) ? this.storeUser.role_members : [];
      return rm.map(m => m.role?.id).filter(id => id != null);
    },
    userRoleNames() {
      const rm = Array.isArray(this.storeUser.role_members) ? this.storeUser.role_members : [];
      return rm.map(m => m.role?.name).filter(Boolean);
    },
    // Classify the user's role into a coaching profile based on the user_roles
    // table IDs (authoritative list):
    //   1 Super Admin | 2 Admin | 3 Agent | 4 IT Developers | 5 GHL Developers
    //   6 Marketing   | 7 Design | 8 Sales | 9 Product Developers
    //   10 Recruitment | 11 Management | 12 Operations | 13 Finance/Accounts
    // Maps a user_roles.id → coaching profile key. Keeps the ID-to-profile logic
    // in one place so it stays consistent between primary and secondary detection.
    idToProfile() {
      return {
        1: 'superadmin',
        2: 'management',
        3: 'agent',
        4: 'engineering',
        5: 'engineering',
        6: 'marketing',
        7: 'design',
        8: 'sales',
        9: 'engineering',
        10: 'recruitment',
        11: 'management',
        12: 'operations',
        13: 'finance',
      };
    },
    // Priority order for picking the PRIMARY profile when a user has multiple roles.
    // Higher = wins. Super admin always wins. Management beats specialist roles
    // because leading a team changes the coaching lens more than any single function.
    profilePriority() {
      return {
        superadmin: 100,
        management: 90,
        sales: 70,
        agent: 65,
        engineering: 60,
        recruitment: 55,
        marketing: 50,
        design: 45,
        operations: 40,
        finance: 35,
        generic: 0,
      };
    },
    // Profiles derived from the roles the user PERSONALLY holds (role_members).
    // This represents the work they do themselves — not the teams they manage.
    memberProfiles() {
      const ids = new Set(this.userRoleIds);
      const legacy = this.storeUser.role_id;
      if (legacy === 1) ids.add(1);
      if (legacy === 11) ids.add(11);
      if (legacy === 2) ids.add(2);

      const profiles = new Set();
      ids.forEach(id => {
        const p = this.idToProfile[id];
        if (p) profiles.add(p);
      });
      return Array.from(profiles);
    },
    // Profiles the user MANAGES (but doesn't necessarily do themselves).
    // e.g. "Manager of Sales" → ['sales']. Used to tell the AI what their team does.
    managedProfiles() {
      const ids = new Set(this.managedRoleIds);
      const profiles = new Set();
      ids.forEach(id => {
        const p = this.idToProfile[id];
        if (p) profiles.add(p);
      });
      return Array.from(profiles);
    },
    // All distinct profiles to rank for primary-lens selection.
    // If user manages ANY team, management profile is included (so manager lens wins).
    // Member profiles (work they personally do) are also included for multi-role users.
    allProfiles() {
      const set = new Set(this.memberProfiles);
      // Managing a team is its own coaching lens — add management if user manages anyone.
      if (this.managedProfiles.length > 0 || this.isPeopleManager) set.add('management');

      const arr = Array.from(set).sort(
        (a, b) => (this.profilePriority[b] || 0) - (this.profilePriority[a] || 0)
      );
      return arr.length > 0 ? arr : ['generic'];
    },
    // The winning profile (used for vocab, tone, and the primary coaching lens).
    roleProfile() {
      return this.allProfiles[0];
    },
    // Everything OTHER than the primary — used to tell the AI the user's other hats.
    secondaryProfiles() {
      return this.allProfiles.slice(1);
    },
    // True when the user genuinely wears multiple meaningful hats (not just one role).
    isMultiRole() {
      return this.allProfiles.length > 1;
    },
    managedRoleIds() {
      const uid = this.storeUser.id;
      const managers = typeof this.allManagers === 'function' ? this.allManagers() : [];
      if (!Array.isArray(managers) || !uid) return [];
      return managers
        .filter(m => m.user_id === uid && (m.is_manager === true || m.is_manager === 1 || m.is_manager === undefined))
        .map(m => m.user_role_id)
        .filter(id => id != null);
    },
    // Human-readable role label used in prompt phrasing.
    roleLabel() {
      const name = this.userRoleNames[0];
      if (name) return name;
      return this.primaryRoleLabel;
    },
    // Vocabulary bundle tuned per role profile. Used to swap the words "task",
    // "deal", "ticket", etc. so coaching sounds native to the user's work.
    roleVocab() {
      const profile = this.roleProfile;
      const bundles = {
        // role_id 1 — Super Admin (company-wide executive lens)
        superadmin: {
          unit: 'initiative', units: 'initiatives', verb: 'move forward',
          overdueFrame: 'open decisions or cross-team blockers that are overdue',
          momentumFrame: 'company-wide initiatives',
          habitFlavor: 'leadership rhythm (weekly org review, inbox zero, 1-on-1 prep)',
          kpiFlavor: 'company-level outcomes (team output, cycle time, bottleneck rate)',
          stopFlavor: 'is pulling you into the weeds instead of leading',
        },
        // role_id 2 Admin + role_id 11 Management — team leadership lens
        management: {
          unit: 'task', units: 'tasks', verb: 'move forward',
          overdueFrame: 'overdue items and decisions waiting on you',
          momentumFrame: 'active work across your team',
          habitFlavor: 'weekly 1-on-1s, daily unblock check, feedback cadence',
          kpiFlavor: 'team KPIs (throughput, cycle time, people-development wins)',
          stopFlavor: 'is pulling you away from your team',
        },
        // role_id 8 — Sales
        sales: {
          unit: 'deal', units: 'deals', verb: 'advance',
          overdueFrame: 'cooling follow-ups and slipping deals',
          momentumFrame: 'active deals',
          habitFlavor: 'prospecting block, daily follow-up discipline, CRM hygiene',
          kpiFlavor: 'sales KPIs (calls, demos booked, pipeline created, closes)',
          stopFlavor: 'is stealing time from real selling',
        },
        // role_id 3 — Agent (client-facing support/ops rep)
        agent: {
          unit: 'ticket', units: 'tickets', verb: 'resolve',
          overdueFrame: 'aging tickets and client requests past SLA',
          momentumFrame: 'open tickets',
          habitFlavor: 'queue triage, daily follow-up discipline, knowledge base updates',
          kpiFlavor: 'agent KPIs (first response time, resolution time, CSAT)',
          stopFlavor: 'is pulling you off the live queue',
        },
        // role_ids 4 IT Dev + 5 GHL Dev + 9 Product Dev
        engineering: {
          unit: 'ticket', units: 'tickets', verb: 'ship',
          overdueFrame: 'stale PRs and tickets blocking the team',
          momentumFrame: 'active tickets',
          habitFlavor: 'daily PR review, test-first discipline, focus blocks',
          kpiFlavor: 'engineering KPIs (PR cycle time, tickets shipped, bug rate)',
          stopFlavor: 'is keeping you in shallow context-switching instead of deep work',
        },
        // role_id 6 — Marketing
        marketing: {
          unit: 'campaign', units: 'campaigns', verb: 'launch',
          overdueFrame: 'stalled campaigns and content past its ship date',
          momentumFrame: 'live campaigns',
          habitFlavor: 'weekly content review, daily analytics check, brief writing',
          kpiFlavor: 'marketing KPIs (MQLs, CAC, content output, engagement)',
          stopFlavor: 'is low-signal activity instead of pipeline-building work',
        },
        // role_id 7 — Design
        design: {
          unit: 'deliverable', units: 'deliverables', verb: 'ship',
          overdueFrame: 'overdue design deliverables and feedback rounds',
          momentumFrame: 'active design work',
          habitFlavor: 'daily design review, feedback turnaround, design-system hygiene',
          kpiFlavor: 'design KPIs (deliverables shipped, review turnaround, iteration count)',
          stopFlavor: 'is polish-perfectionism instead of shipping',
        },
        // role_id 10 — Recruitment
        recruitment: {
          unit: 'candidate', units: 'candidates', verb: 'close',
          overdueFrame: 'candidates and open roles waiting on you',
          momentumFrame: 'active pipelines',
          habitFlavor: 'candidate follow-up block, daily sourcing, pipeline review',
          kpiFlavor: 'recruitment KPIs (time-to-hire, pipeline coverage, offer acceptance rate)',
          stopFlavor: 'is distracting you from candidate-facing work',
        },
        // role_id 12 — Operations
        operations: {
          unit: 'task', units: 'tasks', verb: 'resolve',
          overdueFrame: 'overdue ops items and blocked approvals',
          momentumFrame: 'active ops work',
          habitFlavor: 'daily inbox triage, weekly vendor check, process review',
          kpiFlavor: 'ops KPIs (turnaround time, SLA %, cost per task)',
          stopFlavor: 'is reactive firefighting instead of process improvement',
        },
        // role_id 13 — Finance / Accounts
        finance: {
          unit: 'task', units: 'tasks', verb: 'close',
          overdueFrame: 'overdue reconciliations and approvals',
          momentumFrame: 'active finance work',
          habitFlavor: 'daily reconciliation block, weekly cash review, approval SLA',
          kpiFlavor: 'finance KPIs (close-cycle days, DSO, forecast accuracy)',
          stopFlavor: 'is keeping you in low-leverage admin',
        },
        generic: {
          unit: 'task', units: 'tasks', verb: 'finish',
          overdueFrame: 'overdue commitments',
          momentumFrame: 'active work',
          habitFlavor: 'daily planning, focus block, end-of-day review',
          kpiFlavor: 'weekly outcome KPIs that match your role',
          stopFlavor: "is wasting your time and isn't moving real outcomes",
        },
      };
      return bundles[profile] || bundles.generic;
    },
    aiPrompts() {
      // Role-aware coaching prompts. The vocabulary + framing swap based on the
      // user's detected role profile (superadmin, sales, engineering, HR, etc.)
      // so the coach speaks the user's language and references their real items.
      const prompts = [];
      const role = this.roleLabel;
      const v = this.roleVocab;
      const profile = this.roleProfile;
      const overdue = this.overdueTasks;
      const dueToday = this.dueTodayTasks;
      const inProg = this.inProgressTasks;
      const highPri = this.highPriorityPending;
      const wins = this.completedThisWeek.length;
      const pendingKpis = this.pendingKpis;

      // --- 1. URGENCY — overdue first, reframed per role.
      if (overdue.length >= 3) {
        const worst = overdue
          .slice()
          .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)))[0];
        prompts.push(`I have ${overdue.length} ${v.overdueFrame} — the oldest is "${worst.name}". Help me triage which to tackle first.`);
      } else if (overdue.length > 0) {
        const t = overdue[0];
        if (profile === 'sales') {
          prompts.push(`"${t.name}" is overdue. Write me a re-engagement message and the best next step to revive it.`);
        } else {
          prompts.push(`"${t.name}" is overdue. How should I recover without dropping everything else?`);
        }
      } else if (highPri.length >= 2) {
        prompts.push(`I have ${highPri.length} high-priority ${v.units} still open. Which one should I push hardest on today?`);
      } else if (dueToday.length > 0) {
        prompts.push(`${dueToday.length} ${dueToday.length === 1 ? v.unit : v.units} due today. What's the right order to work them?`);
      } else {
        prompts.push(`No fires today — where should I invest my time as a ${role} to build momentum for next week?`);
      }

      // --- 2. MOMENTUM / FOCUS.
      if (inProg.length >= 3) {
        prompts.push(`I have ${inProg.length} ${v.units} in flight at once — am I spreading myself too thin? Help me pick the 1-2 most important.`);
      } else if (inProg.length === 1) {
        prompts.push(`I'm in the middle of "${inProg[0].name}". Coach me on the next step to ${v.verb} it.`);
      } else if (wins === 0 && this.allTasks.length > 0) {
        prompts.push(`I haven't ${v.verb === 'finish' ? 'finished' : v.verb + 'd'} anything this week. What's one high-impact ${v.unit} I should commit to today?`);
      } else if (wins >= 5) {
        prompts.push(`I ${v.verb === 'finish' ? 'finished' : v.verb + 'd'} ${wins} ${v.units} this week — how do I keep this momentum without burning out?`);
      } else {
        prompts.push(`Review my ${v.momentumFrame} and tell me where I'm leaking time.`);
      }

      // --- 3. KPI / METRICS lens.
      if (pendingKpis.length > 0) {
        const kpi = pendingKpis[0];
        prompts.push(`My KPI "${kpi.text}" is still open this week. What actions close that gap the fastest?`);
      } else if ((this.kpis || []).length > 0) {
        prompts.push(`All my KPIs are hit this week — what stretch target should I set next as a ${role}?`);
      } else {
        prompts.push(`I'm a ${role}. What 3 ${v.kpiFlavor} should I be tracking right now?`);
      }

      // --- 4. ROLE-SPECIFIC COACHING.
      // If the user manages one or more teams, their last prompt is always team-focused
      // (leadership is their highest-leverage lens, regardless of other hats).
      if (this.managedRoleNames.length >= 2) {
        const teams = this.managedRoleNames.slice(0, 3).join(' & ');
        prompts.push(`I lead the ${teams} teams. How should I split my attention between them this week?`);
      } else if (this.managedRoleNames.length === 1) {
        const team = this.managedRoleNames[0];
        prompts.push(`As leader of the ${team} team, what coaching topic should I run in this week's 1-on-1s?`);
      } else if (this.isMultiRole && this.userRoleNames.length >= 2) {
        const hats = this.userRoleNames.slice(0, 3).join(' + ');
        prompts.push(`I wear multiple hats (${hats}). Help me decide what to prioritise today across them.`);
      } else if (profile === 'superadmin') {
        prompts.push(`As Super Admin, which team or area looks like the biggest bottleneck right now — and what should I unblock first?`);
      } else if (profile === 'management' || this.isPeopleManager) {
        prompts.push(`As a manager, what's one team behaviour I should reinforce and one I should correct this week?`);
      } else if (!this.hasHabits) {
        prompts.push(`What's one daily habit (${v.habitFlavor}) that would compound fastest for a ${role}?`);
      } else if (this.habits.length > 0) {
        const topStreak = [...this.habits].sort((a, b) => (b.streak || 0) - (a.streak || 0))[0];
        if (topStreak && topStreak.streak >= 7) {
          prompts.push(`My "${topStreak.text}" habit is on a ${topStreak.streak}-day streak. What discipline should I add next to level up as a ${role}?`);
        } else {
          prompts.push(`Review my habits and tell me which one to double down on this week.`);
        }
      } else {
        prompts.push(`What's one activity I should stop doing that ${v.stopFlavor}?`);
      }

      // De-duplicate and cap at 4.
      const seen = new Set();
      return prompts.filter(p => {
        const k = p.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      }).slice(0, 4);
    },
  },
  mounted() {
    this.loadHistory();
    this.loadContext();
    // Sync with global growth bus so AI session history + context refresh
    // whenever other cards mutate data (task completion, KPI, habits, etc.).
    this.$root.$on('growth:refresh', this.onGrowthRefresh);
  },
  beforeDestroy() {
    this.$root.$off('growth:refresh', this.onGrowthRefresh);
  },
  methods: {
    onGrowthRefresh() {
      this.loadHistory();
      this.loadContext();
    },
    async loadContext() {
      try {
        const [kpis, habits, areas] = await Promise.all([
          this.$axios.get('api/employee-growth/kpis'),
          this.$axios.get('api/employee-growth/habits'),
          this.$axios.get('api/employee-growth/areas'),
        ]);
        if (kpis.data.response) {
          // KPI endpoint returns { this_week: [...], last_week: [...] } — flatten for presence checks.
          const payload = kpis.data.data || {};
          if (Array.isArray(payload)) {
            this.kpis = payload;
          } else {
            this.kpis = [...(payload.this_week || []), ...(payload.last_week || [])];
          }
        }
        if (habits.data.response) this.habits = Array.isArray(habits.data.data) ? habits.data.data : [];
        if (areas.data.response) this.growthAreas = Array.isArray(areas.data.data) ? areas.data.data : [];
      } catch (e) { /* non-critical — context will just be thin */ }
    },
    async loadHistory() {
      this.loading = true;
      try {
        const res = await this.$axios.get('api/employee-growth/ai-sessions');
        if (res.data.response) this.sessionHistory = res.data.data || [];
      } finally {
        this.loading = false;
      }
    },
    async saveSession(prompt, reply) {
      try {
        // Coach replies are goal-directed → high-impact tier (5 pts, spec).
        const res = await this.$axios.post('api/employee-growth/ai-sessions', {
          prompt,
          response: reply,
          is_high_impact: true,
        });
        console.log('[AiCoach] saveSession response:', JSON.stringify(res.data));
        if (res.data.response) this.sessionHistory.unshift(res.data.data);
      } catch (e) {
        console.error('[AiCoach] saveSession error:', e);
      }
    },
    dismissWarning() {
      this.warningDismissed = true;
    },
    async regenAiSuggestions() {
      this.aiSuggestions = [];
      await this._loadAiSuggestions();
    },
    async openAiSuggestPanel() {
      this.showAiSuggestPanel = !this.showAiSuggestPanel;
      if (this.showAiSuggestPanel && !this.aiSuggestionsLoading) {
        await this._loadAiSuggestions();
      }
    },
    async _loadAiSuggestions() {
      if (this.aiSuggestionsLoading) return;
      this.aiSuggestionsLoading = true;
      try {
        const userCtx = buildUserContext(this, { kpis: this.kpis, growthAreas: this.growthAreas, habits: this.habits });
        const overdueSummary = this.overdueTasks.slice(0, 3).map(t => `"${t.name}"`).join(', ') || 'none';
        const pendingKpiSummary = this.pendingKpis.slice(0, 3).map(k => `"${k.text}"`).join(', ') || 'none';
        const winsSummary = this.completedThisWeek.length;
        const system = `You are an AI coach for a ${this.roleLabel}.
Generate exactly 4 highly specific coaching questions or prompts grounded in the user's real situation below.
Each prompt should help the user get actionable coaching from the AI on what they are currently dealing with.
Focus on: productivity, task prioritisation, KPI achievement, habit building, or career growth.
Return ONLY a JSON array with exactly 4 objects: [{"text": "coaching question (max 20 words)", "reason": "why this is relevant right now (max 12 words)"}]
SIGNALS:
- Overdue tasks: ${overdueSummary}
- Pending KPIs: ${pendingKpiSummary}
- Wins this week: ${winsSummary}
USER CONTEXT:
${userCtx}`;
        const raw = await askAI(
          'Suggest 4 personalized coaching prompts based on my current situation.',
          system,
          { logSession: false, isHighImpact: false },
        );
        this.aiSuggestions = parseAIJson(raw);
      } catch (e) {
        this.$toast && this.$toast.error('Failed to load AI suggestions');
      } finally {
        this.aiSuggestionsLoading = false;
      }
    },
    selectPrompt(prompt) {
      this.activePrompt = prompt;
      this.aiReply = null;
      this.getAiReply(prompt);
    },
    async sendQuery() {
      if (!this.aiQuery.trim()) return;
      const prompt = this.aiQuery.trim();
      this.aiQuery = '';
      this.activePrompt = null;
      await this.getAiReply(prompt);
    },
    // Build a role-specific system prompt so the coach speaks the user's language
    // (super admin vs developer vs sales vs finance, etc.). Uses the detected
    // roleProfile to pick the lens, rules, and vocabulary.
    buildSystemPrompt(userCtx) {
      const profile = this.roleProfile;
      const role = this.roleLabel;
      const v = this.roleVocab;

      // Per-role coaching lens (what "tasks/KPIs/habits" mean in their world).
      const lensByProfile = {
        superadmin: `COACHING LENS — cross-functional executive
- Tasks = strategic initiatives, cross-team decisions, and unblocks.
- KPIs = company-level outcomes (throughput across teams, cycle time, bottleneck rate, team health).
- Habits = leadership rhythm (weekly org reviews, decision logs, 1-on-1 prep, inbox zero).
- Frame advice around: where the company is bottlenecked, which team needs your attention, and what high-leverage decision you should make first.`,
        management: `COACHING LENS — team leader
- Tasks = things you owe your team (decisions, unblocks, reviews) or items your team owes you.
- KPIs = team throughput, cycle time, and people-development wins.
- Habits = weekly 1-on-1s, daily unblock check, feedback cadence.
- Frame advice around: what's blocking your team, who needs coaching, and where you should delegate vs. do.`,
        sales: `COACHING LENS — sales
- Tasks = follow-ups, outreach, demos, discovery calls, proposals, closes. Overdue tasks = cooling deals.
- KPIs = activity metrics (calls, demos, pipeline created) and outcomes (deals advanced, win rate).
- Habits = prospecting block, daily follow-up discipline, CRM hygiene, call reviews.
- Frame advice around: building pipeline, advancing deals, protecting momentum, raising activity quality.
- When asked to draft messages (re-engagement, follow-up, proposal intro), write in user's voice: short, specific, prospect-focused, ending with one clear next step.`,
        agent: `COACHING LENS — client-facing agent
- Tasks = open tickets, client requests, follow-ups. Overdue = SLA at risk.
- KPIs = first-response time, resolution time, CSAT, ticket volume.
- Habits = queue triage, daily follow-ups, knowledge base updates.
- Frame advice around: keeping the queue clean, hitting SLAs, and turning tickets into good client experience.`,
        engineering: `COACHING LENS — engineering (${role})
- Tasks = tickets, PRs, bug fixes, deliverables. Overdue = stuck tickets or stale PRs blocking others.
- KPIs = PR cycle time, tickets shipped, bug rate, deploy frequency.
- Habits = daily PR review, test-first discipline, deep-work focus blocks.
- Frame advice around: shipping velocity, code quality, reducing work-in-progress, and protecting deep-work time.`,
        marketing: `COACHING LENS — marketing
- Tasks = campaigns, content pieces, launches, briefs. Overdue = stalled campaigns.
- KPIs = MQLs, CAC, content output, engagement, pipeline contribution.
- Habits = weekly content review, daily analytics check, brief writing.
- Frame advice around: pipeline-building activity vs polish, test-and-learn loops, and content cadence.`,
        design: `COACHING LENS — design
- Tasks = design deliverables, feedback rounds, iterations. Overdue = blocked handoffs.
- KPIs = deliverables shipped, review turnaround, iteration count.
- Habits = daily design review, fast feedback turnaround, design-system hygiene.
- Frame advice around: shipping vs over-polishing, fast feedback loops, and consistent craft.`,
        recruitment: `COACHING LENS — recruitment
- Tasks = candidate follow-ups, interview scheduling, offer prep. Overdue = candidates going cold.
- KPIs = time-to-hire, pipeline coverage per role, offer acceptance rate, interview SLA.
- Habits = daily sourcing block, candidate follow-up, pipeline review.
- Frame advice around: keeping candidates warm, closing offers, and pipeline coverage per open role.
- When asked to draft candidate messages, write short, human, specific notes ending with one clear next step.`,
        operations: `COACHING LENS — operations
- Tasks = approvals, vendor work, process items, ops tickets. Overdue = breaking SLAs upstream.
- KPIs = turnaround time, SLA %, cost per task, error rate.
- Habits = daily inbox triage, weekly vendor check, process review.
- Frame advice around: reducing firefighting, improving process, and hitting SLAs predictably.`,
        finance: `COACHING LENS — finance / accounts
- Tasks = reconciliations, approvals, reports, close activities. Overdue = delayed books.
- KPIs = close-cycle days, DSO, forecast accuracy, approval SLA.
- Habits = daily reconciliation block, weekly cash review, approval SLA.
- Frame advice around: close cadence, clean books, and staying ahead of month-end crunch.`,
        generic: `COACHING LENS — ${role}
- Tasks = your committed work. Overdue = ${v.overdueFrame}.
- KPIs = weekly outcome metrics that match your role.
- Habits = daily planning, focus block, end-of-day review.
- Frame advice around: protecting focus, clearing overdue work, and compounding daily habits.`,
      };
      const lens = lensByProfile[profile] || lensByProfile.generic;

      // Per-role tone hint.
      const toneByProfile = {
        superadmin: 'a seasoned chief-of-staff who has run multiple teams',
        management: 'a seasoned team lead who has coached many directs',
        sales: 'a veteran sales manager who has carried a bag',
        agent: 'a support lead who has lived in the queue',
        engineering: 'a staff engineer / tech lead who values shipping',
        marketing: 'a growth-marketing lead who ships campaigns weekly',
        design: 'a design lead who has shipped many products',
        recruitment: 'a veteran recruiter who closes tough roles',
        operations: 'an ops lead who has built scalable processes',
        finance: 'a finance manager who runs a clean close',
        generic: 'a seasoned performance coach',
      };
      const tone = toneByProfile[profile] || toneByProfile.generic;

      // Multi-role disclosure: list every hat the user wears so the AI can blend
      // advice when work spans roles (e.g. someone who is Sales + Marketing).
      const allHats = this.userRoleNames.length > 0 ? this.userRoleNames.join(', ') : role;
      const secondaryBlock = this.secondaryProfiles.length > 0
        ? `\nSECONDARY HATS: the user ALSO wears these roles → ${this.secondaryProfiles.join(', ')}.\n- Keep the PRIMARY lens above as your default voice.\n- When a task, KPI, or question clearly belongs to a secondary hat (e.g. a marketing campaign for a Sales+Marketing user), switch into that lens for that specific point — don't force everything through the primary lens.\n- If the user asks "how do I balance my hats?", help them sequence work across roles by impact and urgency.\n`
        : '';

      // Manager-of-team disclosure: if the user manages specific teams, tell the AI
      // which teams and what work those teams do (so it can coach their reports).
      const managedTeams = this.managedRoleNames;
      // Manager directory — users with role_id 1/2/11, with team names from allManagers + roles
      const allUsersList = typeof this.users === 'function' ? this.users() : [];
      const allMgrRecords = typeof this.allManagers === 'function' ? this.allManagers() : [];
      const rolesList = typeof this.roles === 'function' ? this.roles() : [];
      // Build map: user_id → [team names] from UserRoleMember records
      const userTeamMap = {};
      for (const m of allMgrRecords) {
        if (!m.user_id) continue;
        const roleName = rolesList.find(r => r.id === m.user_role_id)?.name;
        if (!roleName) continue;
        if (!userTeamMap[m.user_id]) userTeamMap[m.user_id] = [];
        userTeamMap[m.user_id].push(roleName);
      }
      const managers = allUsersList
        .filter(u => [1, 2, 11].includes(u.role_id))
        .map(u => ({ ...u, teams: userTeamMap[u.id] || [] }));
      console.log('[AiCoachCard] managers with teams:', managers.map(m => ({ id: m.id, name: m.name, role_id: m.role_id, teams: m.teams, email: m.email })));
      const managerDirectoryBlock = managers.length > 0
        ? `\nCOMPANY MANAGER DIRECTORY (use this when user asks who to contact)\n${managers.map(m => {
            const teamPart = m.teams?.length ? ' [Team: ' + m.teams.join(', ') + ']' : '';
            const posPart = m.position_name ? ' (' + m.position_name + ')' : '';
            return `- ${m.name}${teamPart}${posPart}: ${m.email || 'no email on file'}`;
          }).join('\n')}\n- When the user asks who to contact about a topic or issue, suggest the most relevant manager by name and email based on their team. Pick the best 1-2 matches only — do NOT list all managers.\n`
        : '';
      const managedLensHints = this.managedProfiles
        .map(p => {
          const hints = {
            sales: 'Sales team — pipeline, prospecting, deals, close rate',
            engineering: 'Engineering team — PRs, shipping velocity, code quality, deep work',
            agent: 'Agent team — ticket queue, SLA, CSAT',
            marketing: 'Marketing team — campaigns, content output, MQLs',
            design: 'Design team — deliverables, feedback loops, craft',
            recruitment: 'Recruitment team — candidate pipeline, time-to-hire',
            operations: 'Operations team — SLAs, process, turnaround time',
            finance: 'Finance team — close cycle, reconciliations, forecast accuracy',
          };
          return hints[p];
        })
        .filter(Boolean);
      const managerBlock = managedTeams.length > 0
        ? `\nTEAMS THE USER MANAGES: ${managedTeams.join(', ')}.\n${managedLensHints.length > 0 ? '- What those teams care about: ' + managedLensHints.join(' | ') + '.\n' : ''}- The user is a MANAGER of these teams — coaching advice for these teams should be about THEIR TEAM'S work, not the user's own hands-on work.\n- Frame leadership advice as: how to coach reports, unblock the team, run effective 1-on-1s, set the right KPIs for the team, and identify who on the team needs attention.\n- When multiple managed teams exist, help the user decide how to split attention across them by impact and urgency.\n`
        : '';

      return `You are an elite FOCUS COACH embedded inside the user's workflow app.
The user's role is **${role}** (primary profile: ${profile}).
All roles the user holds: ${allHats}.
Your job is to keep the user laser-focused on the work that actually moves their role's outcomes — not generic productivity advice.
You have LIVE access to the user's real tasks, KPIs, habits, workload, and role assignments.

${lens}
${secondaryBlock}${managerBlock}${managerDirectoryBlock}
RULES
- Speak to the user directly as "you". Be warm but direct, like ${tone} — not corporate.
- ALWAYS reference specific items from their context by name when relevant (e.g. "your '${v.units === 'deals' ? 'Follow up with Acme' : v.units === 'tickets' ? 'Fix login bug #124' : 'Q2 report'}' is 3 days overdue").
- Prioritise urgency: overdue ${v.units} > ${v.units} due today > ${v.units} in progress > KPIs > habits.
- If the user has overdue work, address it first.
- If the user has no KPIs or habits, recommend 2-3 concrete ones tailored to their role.
- Give exactly 3-5 short sentences (or 3-5 bullet points if listing). No fluff, no filler openers ("Great question!"), no closing summaries.
- Use markdown sparingly: **bold** only for the most important 1-2 phrases per response.
- FORMATTING RULE: When listing 3+ items (${v.units}, KPIs, habits, etc.), ALWAYS use a bullet list (- item), NEVER comma-separated inline text. Keep each bullet to one line.
- Do NOT dump all ${v.units} in a single paragraph. Group by theme or priority; show top 5 then say "and X more".
- If you don't have enough context to answer well, say so in one line and point to what they should add.
- Never invent ${v.units}, KPIs, or habits that aren't in the context.

USER CONTEXT (live data):
${userCtx}`;
    },
    async getAiReply(prompt) {
      this.aiThinking = true;
      this.aiReply = null;
      try {
        const userCtx = buildUserContext(this, {
          kpis: this.kpis,
          habits: this.habits,
          growthAreas: this.growthAreas,
        });
        const system = this.buildSystemPrompt(userCtx);
        const reply = await askAI(prompt, system);
        this.aiReply = reply;
        await this.saveSession(prompt, reply);
        // log AI engagement points so GrowthScoreCard reflects AI usage
        this.$root.$emit('growth:refresh');
      } catch (e) {
        this.aiReply = 'Sorry, I could not connect to the AI coach right now. Please try again.';
      } finally {
        this.aiThinking = false;
      }
    },
    renderMarkdown(text) {
      if (!text) return '';
      // Escape raw HTML to prevent XSS
      let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      // Bold: **text**
      html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      // Italic: *text* (single, not already consumed)
      html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
      // Bullet list lines: lines starting with - or •
      const lines = html.split('\n');
      const result = [];
      let inList = false;
      for (const line of lines) {
        const bullet = line.match(/^[\s]*[-•]\s+(.*)/);
        if (bullet) {
          if (!inList) { result.push('<ul style="margin:6px 0 6px 16px;padding:0;">'); inList = true; }
          result.push(`<li style="margin-bottom:3px;">${bullet[1]}</li>`);
        } else {
          if (inList) { result.push('</ul>'); inList = false; }
          result.push(line === '' ? '<br>' : `<span>${line}</span><br>`);
        }
      }
      if (inList) result.push('</ul>');
      return result.join('');
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      return this.$date ? this.$date(dateStr).fromNow() : dateStr.slice(0, 10);
    },
  },
};
</script>
